import { Reveal } from "@components/ui";
import { Link } from "react-router-dom";

const techCards = [
  {
    title: "Smart Metering",
    description:
      "Advanced meters providing real-time consumption data for optimized usage",
    accent: "#1a7a4f",
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <path
          d="M14 3.5V7M14 21v3.5M3.5 14H7M21 14h3.5M6.1 6.1l2.45 2.45M19.45 19.45l2.45 2.45M6.1 21.9l2.45-2.45M19.45 8.55l2.45-2.45"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <circle cx="14" cy="14" r="5" stroke="currentColor" strokeWidth="2" />
        <path d="M14 11v3l2 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: "ERP & Billing Systems",
    description:
      "Integrated platforms for efficient billing, tracking, and customer management",
    accent: "#122d63",
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <rect x="3" y="5" width="22" height="18" rx="3" stroke="currentColor" strokeWidth="2" />
        <path d="M3 11h22M10 11v12" stroke="currentColor" strokeWidth="2" />
        <path d="M14 16h7M14 20h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    title: "SCADA Monitoring",
    description:
      "Real-time supervisory control for network safety and operational efficiency",
    accent: "#f03813",
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <rect x="4" y="4" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2" />
        <path d="M10 22h8M14 18v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M8 11l3-3 3 4 4-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: "Leak Detection",
    description:
      "AI-powered leak detection systems protecting communities and the environment",
    accent: "#6b4fc7",
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <path
          d="M14 3s-7 8.5-7 14a7 7 0 0014 0c0-5.5-7-14-7-14z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <path d="M11 19a3 3 0 003 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    title: "Mobile Workforce",
    description:
      "Digital field operations for faster response and reduced carbon footprint",
    accent: "#d4880f",
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <rect x="8" y="3" width="12" height="22" rx="3" stroke="currentColor" strokeWidth="2" />
        <path d="M12 21h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M12 8h4M12 12h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
];

const SmartTechSection = () => {
  return (
    <section className="py-12 md:py-16 lg:py-[120px] px-5 md:px-10 lg:px-[180px]">
      <div className="w-full max-w-[1368px] mx-auto">
        <Reveal>
          <div className="flex flex-col items-center text-center mb-10 md:mb-14">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#6b4fc7]/10 text-[#6b4fc7] text-[14px] md:text-[16px] font-medium tracking-[-0.04em] mb-4">
              Technology
            </span>
            <h2 className="text-[28px] md:text-[44px] lg:text-[60px] tracking-[-0.04em] font-semibold text-[#122d63]">
              Technology That Saves Energy and Lives
            </h2>
            <p className="text-[20px] md:text-[24px] lg:text-[28px] text-[#62636c] tracking-[-0.04em] mt-3 max-w-[700px]">
              Smart solutions powering efficient and safe operations
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10 md:mb-14">
          {techCards.map((card, index) => (
            <Reveal key={index} className="h-full">
              <div className="group relative overflow-hidden rounded-[24px] border border-[rgba(98,99,108,0.2)] bg-white p-6 hover:shadow-[0_4px_20px_rgba(0,0,0,0.08)] hover:border-[#e8e9ed] transition-all duration-300 h-full">
                {/* Top accent bar */}
                <div
                  className="absolute top-0 left-6 right-6 h-[4px] rounded-b-full"
                  style={{ backgroundColor: card.accent }}
                />

                {/* Icon */}
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center mb-5 mt-2 group-hover:scale-110 transition-transform duration-300"
                  style={{
                    backgroundColor: `${card.accent}15`,
                    color: card.accent,
                  }}
                >
                  {card.icon}
                </div>

                <h3 className="text-[20px] md:text-[24px] font-semibold text-[#090d2c] tracking-[-0.04em] mb-2">
                  {card.title}
                </h3>
                <p className="text-[16px] md:text-[20px] text-[rgba(10,14,46,0.75)] tracking-[-0.04em]">
                  {card.description}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        {/* CTA Bar */}
        <Reveal>
          <div className="rounded-[24px] bg-gradient-to-r from-[#122d63] to-[#0a1a3a] px-6 md:px-10 py-6 md:py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-white text-[18px] md:text-[24px] font-semibold tracking-[-0.04em] text-center sm:text-left">
              Explore Our Smart Metering Solutions
            </p>
            <Link
              to="/services/gas-services"
              className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-[#f03813] text-white text-[16px] md:text-[18px] font-semibold tracking-[-0.04em] hover:bg-[#d42f0f] transition-colors duration-300 whitespace-nowrap"
            >
              Learn More
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default SmartTechSection;
